import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatTanggal, formatTanggalInput, num, rupiah } from '../lib/format';
import { Factory, CalendarBlank, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso) - new Date()) / 86400000);
};

export default function Produksi() {
  const [products, setProducts] = useState([]);
  const [productions, setProductions] = useState([]);
  const today = formatTanggalInput(new Date().toISOString());
  const [form, setForm] = useState({ product_id: '', kali_produksi: 1, tanggal_produksi: today, tanggal_expired: '', catatan: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [p, t] = await Promise.all([api.get('/products'), api.get('/transactions')]);
    setProducts(p.data);
    setProductions(t.data.filter((x) => x.type === 'production'));
  };
  useEffect(() => { load(); }, []);

  const selected = products.find((p) => p.id === form.product_id);
  const kali = parseFloat(form.kali_produksi) || 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_id) { toast.error('Pilih produk dulu'); return; }
    setSubmitting(true);
    try {
      await api.post('/productions', {
        product_id: form.product_id,
        kali_produksi: kali || 1,
        tanggal_produksi: form.tanggal_produksi ? new Date(form.tanggal_produksi).toISOString() : null,
        tanggal_expired: form.tanggal_expired ? new Date(form.tanggal_expired).toISOString() : null,
        catatan: form.catatan || null,
      });
      toast.success('Produksi dicatat, stok bahan & produk diperbarui');
      setForm({ ...form, kali_produksi: 1, tanggal_expired: '', catatan: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mencatat produksi');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <form onSubmit={submit} data-testid="form-production" className="lg:col-span-2 bg-card border border-border/60 rounded-xl p-5 lg:p-6 space-y-4 h-fit">
        <div className="flex items-center gap-2">
          <Factory size={24} weight="duotone" className="text-primary" />
          <h3 className="font-display text-lg font-semibold">Catat Produksi</h3>
        </div>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">Produk</span>
          <select value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} data-testid="prodn-product" className={inputCls}>
            <option value="">Pilih produk…</option>
            {products.map((p) => <option key={p.id} value={p.id}>{p.nama}</option>)}
          </select>
        </label>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">Berapa kali resep dibuat?</span>
          <input type="number" step="any" min="0" value={form.kali_produksi} onChange={(e) => setForm({ ...form, kali_produksi: e.target.value })} data-testid="prodn-kali" className={inputCls} />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Tanggal Produksi</span>
            <input type="date" value={form.tanggal_produksi} onChange={(e) => setForm({ ...form, tanggal_produksi: e.target.value })} data-testid="prodn-tgl" className={inputCls} />
          </label>
          <label className="block space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Tanggal Expired</span>
            <input type="date" value={form.tanggal_expired} onChange={(e) => setForm({ ...form, tanggal_expired: e.target.value })} data-testid="prodn-exp" className={inputCls} />
          </label>
        </div>
        <label className="block space-y-1.5">
          <span className="text-sm font-medium text-muted-foreground">Catatan</span>
          <input value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} data-testid="prodn-catatan" className={inputCls} />
        </label>

        {selected && (
          <div className="bg-secondary/50 rounded-lg p-3 text-sm space-y-1" data-testid="prodn-summary">
            <div>Hasil: <b>{num((selected.hasil_per_produksi || 1) * kali, 2)} {selected.satuan_jual}</b></div>
            <div className="text-muted-foreground">Bahan yang akan terpakai:</div>
            <ul className="list-disc pl-5 text-muted-foreground">
              {(selected.metrics?.bahan_detail || []).map((b) => (
                <li key={b.material_id}>{b.nama}: {num(b.qty * kali, 2)} {b.satuan}</li>
              ))}
            </ul>
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="btn-submit-production" className="w-full px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 disabled:opacity-50">
          Simpan Produksi
        </button>
      </form>

      <div className="lg:col-span-3 bg-card border border-border/60 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border/60 flex items-center gap-2">
          <CalendarBlank size={22} weight="duotone" className="text-primary" />
          <h3 className="font-display text-lg font-semibold">Batch Produksi & Expired</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-productions">
            <thead className="bg-secondary/50 text-left">
              <tr>
                <th className="p-3 font-semibold">Produk</th>
                <th className="p-3 font-semibold text-right">Hasil</th>
                <th className="p-3 font-semibold">Produksi</th>
                <th className="p-3 font-semibold">Expired</th>
                <th className="p-3 font-semibold text-right">HPP/unit</th>
              </tr>
            </thead>
            <tbody>
              {productions.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-muted-foreground">Belum ada produksi tercatat.</td></tr>
              )}
              {productions.map((t) => {
                const d = daysLeft(t.tanggal_expired);
                const tone = d === null ? '' : d < 0 ? 'text-rose-600' : d <= 7 ? 'text-amber-600' : 'text-emerald-600';
                return (
                  <tr key={t.id} className="border-t border-border/60" data-testid={`batch-${t.id}`}>
                    <td className="p-3 font-medium">{t.product_nama}</td>
                    <td className="p-3 text-right tabular-nums">{num(t.hasil_units, 2)}</td>
                    <td className="p-3 whitespace-nowrap">{formatTanggal(t.tanggal_produksi)}</td>
                    <td className={`p-3 whitespace-nowrap ${tone}`}>
                      {t.tanggal_expired ? (
                        <span className="inline-flex items-center gap-1">
                          {d <= 7 && <WarningCircle size={14} weight="fill" />}
                          {formatTanggal(t.tanggal_expired)}
                          <span className="text-xs">({d < 0 ? `lewat ${-d} hr` : `${d} hr lagi`})</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-3 text-right tabular-nums">{rupiah(t.hpp_per_unit)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
